import React, { useMemo } from "react";
import { Line } from "react-chartjs-2";
import { Card, CardBody, CardHeader, Col, Row } from "reactstrap";
import { useTranslation } from "react-i18next";

const asNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const formatCount = (value) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(asNumber(value));

const formatDayLabel = (dateKey) => {
  const parts = String(dateKey || "").split("-");
  if (parts.length !== 3) return dateKey || "";
  return `${parts[1]}/${parts[2]}`;
};

const DashboardDailyWebTraffic = ({ data, isLoading, isLightTheme, onDetails }) => {
  const { t } = useTranslation();
  const series = Array.isArray(data?.series) ? data.series : [];
  const today = data?.today || {};
  const last7 = data?.last7 || {};
  const last30 = data?.last30 || {};

  const lineData = useMemo(
    () => ({
      labels: series.map((row) => formatDayLabel(row.date)),
      datasets: [
        {
          label: t("websitePerformance.pageviews"),
          data: series.map((row) => asNumber(row.pageviews)),
          borderColor: "#f97316",
          backgroundColor: "rgba(249, 115, 22, 0.12)",
          pointBackgroundColor: "#f97316",
          pointBorderColor: "#f97316",
          pointRadius: 2,
          pointHoverRadius: 4,
          borderWidth: 2,
          tension: 0.25,
          fill: false,
        },
        {
          label: t("websitePerformance.uniqueVisitors"),
          data: series.map((row) => asNumber(row.unique_visitors)),
          borderColor: "rgba(148, 163, 184, 0.9)",
          backgroundColor: "transparent",
          pointBackgroundColor: "rgba(148, 163, 184, 0.9)",
          pointBorderColor: "rgba(148, 163, 184, 0.9)",
          pointRadius: 2,
          pointHoverRadius: 4,
          borderWidth: 2,
          tension: 0.25,
          fill: false,
        },
      ],
    }),
    [series, t]
  );

  const lineOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: isLightTheme ? "#ffffff" : "#1e1e2f",
          titleColor: isLightTheme ? "#1e293b" : "#f1f5f9",
          bodyColor: isLightTheme ? "#334155" : "#cbd5e1",
          borderColor: isLightTheme ? "#e5e7eb" : "#1e293b",
          borderWidth: 1,
          displayColors: true,
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            color: isLightTheme ? "#6c757d" : "rgba(255,255,255,0.72)",
            font: { size: 10 },
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 10,
          },
        },
        y: {
          beginAtZero: true,
          grid: {
            color: isLightTheme ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.08)",
            drawBorder: false,
          },
          ticks: {
            color: isLightTheme ? "#6c757d" : "rgba(255,255,255,0.72)",
            font: { size: 10 },
            precision: 0,
          },
        },
      },
    }),
    [isLightTheme]
  );

  return (
    <Row>
      <style>
        {`
          .dashboard-web-traffic-legend {
            display: flex;
            gap: 14px;
            font-size: 11px;
            color: rgba(255, 255, 255, 0.7);
          }
          .white-content .dashboard-web-traffic-legend {
            color: #6c757d;
          }
          .dashboard-web-traffic-legend span {
            display: inline-flex;
            align-items: center;
            gap: 6px;
          }
          .dashboard-web-traffic-legend i {
            width: 14px;
            height: 2px;
            display: inline-block;
          }
          .dashboard-web-traffic-foot {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 8px;
            margin-top: 12px;
          }
          .dashboard-web-traffic-foot__cell {
            background: rgba(148,163,184,0.08);
            padding: 8px 10px;
          }
          .dashboard-web-traffic-foot__cell label {
            display: block;
            font-size: 11px;
            font-weight: 500;
            color: rgba(255, 255, 255, 0.7);
            margin-bottom: 2px;
          }
          .white-content .dashboard-web-traffic-foot__cell label {
            color: #6c757d;
          }
          .dashboard-web-traffic-foot__cell strong {
            font-size: 13px;
            font-weight: 700;
            color: #ffffff;
          }
          .white-content .dashboard-web-traffic-foot__cell strong {
            color: #1e293b;
          }
        `}
      </style>
      <Col xs="12" className="card-spacing-fix">
        <Card className="card-chart card-modern">
          <CardHeader>
            <Row className="justify-content-between align-items-center mx-0 mb-3">
              <div className="d-flex align-items-center">
                <i className="tim-icons icon-chart-bar-32 text-info mr-2" />
                <h5 className="card-category mb-0">{t("websitePerformance.dailyWebTraffic")}</h5>
              </div>
              <div className="btn-see-detail" onClick={onDetails}>
                {t("dashboard.details")} <i className="tim-icons icon-double-right ml-1" style={{ fontSize: "0.6rem" }} />
              </div>
            </Row>
            <div className="d-flex justify-content-between align-items-center px-0">
              <span className="card-modern-title">{t("websitePerformance.last30Days")}</span>
              <div className="d-flex align-items-center" style={{ gap: 16 }}>
                <div className="dashboard-web-traffic-legend">
                  <span>
                    <i style={{ background: "rgba(148, 163, 184, 0.9)" }} />
                    {t("websitePerformance.uniqueVisitors")}
                  </span>
                  <span>
                    <i style={{ background: "#f97316" }} />
                    {t("websitePerformance.pageviews")}
                  </span>
                </div>
                <span className="card-modern-value">{isLoading ? "—" : formatCount(last30.pageviews)}</span>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            <div className="chart-area" style={{ height: 210, position: "relative" }}>
              {isLoading && (
                <div className="loading-ghost-overlay">
                  <div className="pulse-loading">{t("websitePerformance.loading")}</div>
                </div>
              )}
              <Line data={lineData} options={lineOptions} />
            </div>
            <div className="dashboard-web-traffic-foot">
              <div className="dashboard-web-traffic-foot__cell">
                <label>{t("websitePerformance.today")}</label>
                <strong>{formatCount(today.pageviews)}</strong>
              </div>
              <div className="dashboard-web-traffic-foot__cell">
                <label>{t("websitePerformance.last7Days")}</label>
                <strong>{formatCount(last7.pageviews)}</strong>
              </div>
              <div className="dashboard-web-traffic-foot__cell">
                <label>{t("websitePerformance.last30Days")}</label>
                <strong>{formatCount(last30.pageviews)}</strong>
              </div>
            </div>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default DashboardDailyWebTraffic;
